import {parseFitFile} from "utils/fit/FitParserUtil.js"; // Make sure to implement or import a valid parser
import {weekNumber} from "weeknumber";
import {DATE_FORMATS, SETTING_SPEED_UNITS, TIME_FORMATS, DB_COLLECTION_NAMES} from "constants/AppConstants.js";
import ConvertUtil from "utils/ConvertUtil.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {ACTIVITY_FOR_OPTIONS, WATERSPORT_OPTIONS} from "utils/DropDownUtil.js";
import GreetUtil from "utils/GreetUtil.js";
import UnitService from "services/UnitService.js";
import ArrayUtil from "utils/ArrayUtil.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import PaceUtil from "utils/PaceUtil.js";
import ThirdPartyIdUtil from "utils/ThirdPartyIdUtil.js";
import {calculateTimeAndDistanceOfPoints, Point, sortDbPoints} from "services/PointService.js";
import {EMPTY_TACKS_AND_JIBES_SERVICE} from "services/TacksAndJibesRecognizerService.js";
import isNil from "lodash/isNil.js";
import cloneDeep from "lodash/cloneDeep.js";
import DirectionUtil from "utils/DirectionUtil.js";
import TokenService from "services/TokenService.js";
import {DbCollectionFactory} from "factories/DbCollectionFactory.js";
import {WSSpeedCalculator} from "wsmath";
import {calculateAverageVMG, calculateVMGForPoints} from "utils/VMGUtil.js";
import {calculateTWA} from "utils/PolarChartUtil.js";
import {isSailingSport} from "utils/SportUtil.js";

export {isSailingSport};

export const ACTIVITY_DEFAULT_VALUES = {
  id: null,
  waterSportId: null,
  durationInUnixTimestamp: 0,
  name: "",
  typeId: 0,
  feelingId: 0,
  bearing: 0,
  from: "",
  to: "",
  city: "",
  country: "",
  pausedTimeInSeconds: 0,
  windAngle: 0,
  windDirection: "N",
  windSpeed: 0,
  durationInSeconds: 0,
  distanceInKm: 0,
  averageSpeedInKM: 0,
  maxSpeedInKM: 0,
  latitude: 0,
  longitude: 0,
  comment: ""
};

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export interface MinAvgMaxHeartRate {
  min: number;
  avg: number;
  max: number;
}

export class Activity {
  activityInDb: any;
  id: any;
  garminActivityId: any;
  suuntoWorkoutKey: any;
  corosWorkoutKey: any;
  fnt: any;
  fntM: any;
  waterSportId: number | null;
  waterSportName: string;
  originalDateInUTC: Date;
  originalDate: Date;
  lastUpdatedDateS: number;
  name: string;
  secondaryName: string;
  deviceName: string | undefined;
  typeId: number;
  typeName: string;
  feelingId: string;
  bearing: number;
  heading: string;
  from: string;
  to: string;
  city: string;
  country: string;
  location: string;
  locationReversed: string;
  windAngle: number;
  windDirection: string;
  windSpeedInKnots: number;
  date: Date;
  dateInDDMMYYYY: string;
  dateInYYYYMMDDTHHMMSSZ: string;
  dateInDDMMMYYYYATHMMWithM: string;
  durationInSeconds: number;
  startDateS: number;
  endDateS: number;
  durationInHHMMSS: string;
  groupByDaily: string;
  groupByWeekly: string;
  groupByMonthly: string;
  groupByYearly: number;
  totalDistanceInKm: number;
  averageSpeedInKM: number;
  maxSpeedInKM: number;
  maxSpeedInMS: number;
  averageSpeedInMS: number;
  latitude: number;
  longitude: number;
  comment: string;
  dbPoints: any[] | undefined;
  navPoints: any[];
  points: any[];
  pausedTimeInSeconds: number;
  pausedTimeInHHMMSS: string;
  bestSpeedFiveSecondsKM: number;
  minTimeOverDistance: number | null;
  averageSpeedOverDistance: number | null;
  calories: number;
  minAvgMaxHeartRate: MinAvgMaxHeartRate;
  minHeartRate: number;
  averageHeartRate: number;
  maxHeartRate: number;
  coords: Coordinate[];
  tacksAndJibesService: any;
  averageVMGInKM: number;
  equipment1: any;
  equipment2: any;
  equipment3: any;
  isDeleted: boolean;
  isSaved: any;
  media: any[];
  // Unit-dependent fields (set lazily by updateUnits)
  totalDistance: number;
  paceWithUnit: any;
  linearDistance: number;
  twoFarthestPointDistance: number;
  distanceUnit: string;
  windSpeedInKM: number;
  windSpeed: number;
  windUnit: string;
  averageSpeed: number;
  maxSpeed: number;
  bestSpeedFiveSeconds: number;
  averageVMG: number;
  bestSpeedTwoSeconds: number;
  bestSpeedTenSeconds: number;
  bestSpeedTwentySeconds: number;
  bestSpeedHundredMeter: number;
  bestSpeed250Meter: number;
  bestSpeedFiveHundredMeter: number;
  bestSpeedOneKM: number;
  bestSpeedOneNM: number;
  bestSpeedOneMi: number;
  bestSpeed5KM: number;
  bestSpeed10KM: number;
  bestSpeed5Mi: number;
  bestSpeed10Mi: number;
  bestSpeed1hour: number;
  speedUnit: string;
  wasPreview: boolean;

  constructor(
    navigation: any,
    dbPoints?: any[],
    customDistanceInMetersForMinTime?: number,
    customDistanceInMetersForAvgSpeed?: number
  ) {
    this.activityInDb = navigation;
    this.id = navigation.ID ?? ACTIVITY_DEFAULT_VALUES.id;
    this.garminActivityId = navigation.gId;
    this.suuntoWorkoutKey = navigation.sId;
    this.corosWorkoutKey = navigation.cId;
    this.fnt = navigation.fnt;
    this.fntM = navigation.fntM;
    this.waterSportId = navigation.tp ?? ACTIVITY_DEFAULT_VALUES.waterSportId;
    this.waterSportName = this.getWaterSportName();
    this.originalDateInUTC = DateTimeUtil.getUTCDateByTimeStamp(
      navigation.dS ?? ACTIVITY_DEFAULT_VALUES.durationInUnixTimestamp
    );
    this.originalDate = DateTimeUtil.getDateByTimeStamp(
      navigation.dS ?? ACTIVITY_DEFAULT_VALUES.durationInUnixTimestamp
    );
    this.lastUpdatedDateS = navigation.lmd ?? ACTIVITY_DEFAULT_VALUES.durationInUnixTimestamp;
    this.name = navigation.nm ?? ACTIVITY_DEFAULT_VALUES.name;
    this.secondaryName = this.getSecondaryName();
    this.deviceName = navigation.devM;
    this.typeId = navigation.tt ?? ACTIVITY_DEFAULT_VALUES.typeId;
    this.typeName = this.getTypeName();
    this.feelingId = getFeelingId(navigation.fe ?? ACTIVITY_DEFAULT_VALUES.feelingId);
    this.bearing = navigation.brg ?? ACTIVITY_DEFAULT_VALUES.bearing;
    this.heading = this.getHeading();
    this.from = navigation.aS ?? ACTIVITY_DEFAULT_VALUES.from;
    this.to = navigation.aE ?? ACTIVITY_DEFAULT_VALUES.to;
    this.city = navigation.ci ?? ACTIVITY_DEFAULT_VALUES.city;
    this.country = navigation.co ?? ACTIVITY_DEFAULT_VALUES.country;
    this.location = this.getLocation();
    this.locationReversed = this.getLocationReversed();
    this.windAngle = navigation.wA ?? ACTIVITY_DEFAULT_VALUES.windAngle;
    this.windDirection = navigation.wD ? navigation.wD : ACTIVITY_DEFAULT_VALUES.windDirection;
    this.windSpeedInKnots = navigation.wS ?? ACTIVITY_DEFAULT_VALUES.windSpeed;
    this.date = DateTimeUtil.removeTimeFromDate(new Date(this.originalDate.getTime()));
    this.dateInDDMMYYYY = DateTimeUtil.convertDateToString(
      new Date(this.originalDate.getTime()),
      DATE_FORMATS.DDMMYYYY
    );
    this.dateInYYYYMMDDTHHMMSSZ = DateTimeUtil.convertDateToString(
      new Date(this.originalDate.getTime()),
      DATE_FORMATS.YYYYMMDDTHHMMSSZ
    );
    this.dateInDDMMMYYYYATHMMWithM = DateTimeUtil.convertDateToString(
      new Date(this.originalDate.getTime()),
      DATE_FORMATS.DDMMMYYYYATHMM_Meridiem
    );
    this.durationInSeconds = navigation.sT ?? ACTIVITY_DEFAULT_VALUES.durationInSeconds;
    this.startDateS = navigation.dS;
    this.endDateS = navigation.dS + this.durationInSeconds;
    this.durationInHHMMSS = DateTimeUtil.convertSecondsToTime(this.durationInSeconds, TIME_FORMATS.HHMMSS);
    this.groupByDaily = this.getGroupByDaily();
    this.groupByWeekly = this.getGroupByWeekly();
    this.groupByMonthly = this.getGroupByMonthly();
    this.groupByYearly = this.getGroupByYearly();
    this.totalDistanceInKm = navigation.dT ?? ACTIVITY_DEFAULT_VALUES.distanceInKm;
    this.averageSpeedInKM = navigation.sA ?? ACTIVITY_DEFAULT_VALUES.averageSpeedInKM;
    this.maxSpeedInKM = navigation.sM ?? ACTIVITY_DEFAULT_VALUES.maxSpeedInKM;
    this.maxSpeedInMS = ConvertUtil.convertSpeed(this.maxSpeedInKM, SETTING_SPEED_UNITS.m_slash_s);
    this.averageSpeedInMS = ConvertUtil.convertSpeed(this.averageSpeedInKM, SETTING_SPEED_UNITS.m_slash_s);
    this.latitude = navigation.lt ?? ACTIVITY_DEFAULT_VALUES.latitude;
    this.longitude = navigation.lg ?? ACTIVITY_DEFAULT_VALUES.longitude;
    this.comment = navigation.dsc ?? ACTIVITY_DEFAULT_VALUES.comment;
    this.dbPoints = dbPoints;
    this.navPoints = this.getPointsFromDbPoints();
    this.points = [...this.navPoints];
    this.pausedTimeInSeconds =
      this.points.reduce((sum, point) => {
        return sum + point.pauseInSecond;
      }, 0) ?? ACTIVITY_DEFAULT_VALUES.pausedTimeInSeconds;
    this.pausedTimeInHHMMSS = DateTimeUtil.convertSecondsToTime(this.pausedTimeInSeconds, TIME_FORMATS.HHMMSS);
    this.bestSpeedFiveSecondsKM = calculateBestAvgSpeed(this.points, 5, 0, SETTING_SPEED_UNITS.km_slash_h)[0];

    // Metrics below are used for challenges
    this.minTimeOverDistance = null;
    if (customDistanceInMetersForMinTime) {
      this.minTimeOverDistance = calculateBestAvgSpeed(this.points, 0, customDistanceInMetersForMinTime, null, true);
    }
    this.averageSpeedOverDistance = null;
    if (customDistanceInMetersForAvgSpeed) {
      this.averageSpeedOverDistance = calculateBestAvgSpeed(
        this.points,
        0,
        customDistanceInMetersForAvgSpeed,
        SETTING_SPEED_UNITS.m_slash_s
      )[0];
    }

    this.calories = navigation.calories ?? this.getCalories();
    this.minAvgMaxHeartRate = calculateHeartRate([...this.points]);
    this.minHeartRate = this.minAvgMaxHeartRate.min;
    this.averageHeartRate = this.minAvgMaxHeartRate.avg;
    this.maxHeartRate = this.minAvgMaxHeartRate.max;
    this.coords = this.getCoordsFromPoints(this.points);
    // Source-of-truth hierarchy for tacks/jibes:
    //   1) SQL cloud store (via useActivityManeuvers.refresh)
    //   2) cloud `recognizeManeuvers` Callable (only on Reset or empty SQL)
    //   3) local TacksAndJibesRecognizer (only when both cloud paths fail)
    // We therefore deliberately do NOT run the local recognizer here at
    // parse time — it would briefly populate the UI with locally-computed
    // maneuvers before SQL overwrites them. Instead we start empty and let
    // useActivityManeuvers swap in the authoritative service. See
    // services/TacksAndJibesRecognizerService.js EMPTY_TACKS_AND_JIBES_SERVICE.
    this.tacksAndJibesService = EMPTY_TACKS_AND_JIBES_SERVICE;

    // Recalculate missing bearings before VMG calculation
    if (this.points && this.points.length > 0) {
      this.points.forEach((point, index) => {
        if (point.bearing < 0 && index > 0 && this.points[index - 1]) {
          const prevPoint = this.points[index - 1];
          if (
            prevPoint.latitude !== undefined &&
            prevPoint.longitude !== undefined &&
            point.latitude !== undefined &&
            point.longitude !== undefined
          ) {
            // Recalculate bearing using DirectionUtil
            point.bearing = DirectionUtil.getBearingValue(
              prevPoint.latitude,
              prevPoint.longitude,
              point.latitude,
              point.longitude
            );
          }
        }
      });
    }

    // Calculate VMG for points and average VMG
    if (this.windAngle >= 0 && this.points && this.points.length > 0) {
      const pointsWithVMG = calculateVMGForPoints(this.points, this.windAngle);
      this.points = pointsWithVMG;
      this.averageVMGInKM = calculateAverageVMG(this.points, this.windAngle);
    } else {
      this.averageVMGInKM = 0;
    }

    // Peak speed on the track must drive session max: navigation sM can be stale or below point peaks
    // (e.g. sync/clean pipeline), which made Wind Analysis "best" region max exceed the activity max.
    if (this.points && this.points.length > 0) {
      const peakMps = this.points.reduce((max, p) => Math.max(max, p.speedInMPS || 0), 0);
      const peakKmh = ConvertUtil.convertMPSToKMH(peakMps);
      if (peakKmh > (this.maxSpeedInKM || 0)) {
        this.maxSpeedInKM = peakKmh;
        this.maxSpeedInMS = ConvertUtil.convertSpeed(this.maxSpeedInKM, SETTING_SPEED_UNITS.m_slash_s);
      }
    }

    this.equipment1 = navigation.eq;
    this.equipment2 = navigation.eq2;
    this.equipment3 = navigation.eq3;
    this.isDeleted = navigation.isDel || false;
    this.isSaved = navigation.sv;
    this.media = [];

    this.updateUnits(undefined, true);
  }

  updateUnits(selectedUnits: any = undefined, preview: boolean = false): void {
    const newWindUnit = selectedUnits?.windUnit || UnitService.getWindUnit();
    const newDistanceUnit = selectedUnits?.distanceUnit || UnitService.getDistanceUnit();
    const newSpeedUnit = selectedUnits?.speedUnit || UnitService.getSpeedUnit();

    // only convert the values that are different from the previous units
    if (this.distanceUnit !== newDistanceUnit || this.wasPreview !== preview) {
      this.totalDistance = ConvertUtil.convertDistance(this.totalDistanceInKm, newDistanceUnit);
      this.paceWithUnit = PaceUtil.calculatePace(this.totalDistanceInKm, this.durationInSeconds, newDistanceUnit, true);

      if (!preview) {
        this.linearDistance = this.linearDistance
          ? ConvertUtil.convertDistance(
              ConvertUtil.convertDistanceToKM(this.linearDistance, this.distanceUnit),
              newDistanceUnit
            )
          : CoordinateUtil.calculateLinearDistance(this.coords, newDistanceUnit);
        this.twoFarthestPointDistance = this.twoFarthestPointDistance
          ? ConvertUtil.convertDistance(
              ConvertUtil.convertDistanceToKM(this.twoFarthestPointDistance, this.distanceUnit),
              newDistanceUnit
            )
          : CoordinateUtil.getDistanceBetweenMostDistantCoordinates(this.coords, newDistanceUnit);
      }

      this.distanceUnit = newDistanceUnit;
    }

    if (this.windUnit !== newWindUnit || this.wasPreview !== preview) {
      this.windSpeedInKM = ConvertUtil.convertKnotTokilometer(this.windSpeedInKnots);
      this.windSpeed = Math.round(ConvertUtil.convertSpeed(this.windSpeedInKM, newWindUnit));

      this.windUnit = newWindUnit;
    }

    if (this.speedUnit !== newSpeedUnit || this.wasPreview !== preview) {
      this.averageSpeed = ConvertUtil.convertSpeed(this.averageSpeedInKM, newSpeedUnit);
      this.maxSpeed = ConvertUtil.convertSpeed(this.maxSpeedInKM, newSpeedUnit);
      this.bestSpeedFiveSeconds = ConvertUtil.convertSpeed(this.bestSpeedFiveSecondsKM, newSpeedUnit);

      // Update VMG with new speed unit
      if (this.averageVMGInKM !== undefined) {
        this.averageVMG = ConvertUtil.convertSpeed(this.averageVMGInKM, newSpeedUnit);
      }

      if (!preview) {
        this.bestSpeedTwoSeconds = this.bestSpeedTwoSeconds
          ? ConvertUtil.convertSpeed(
              ConvertUtil.convertSpeedInKM(this.bestSpeedTwoSeconds, this.speedUnit),
              newSpeedUnit
            )
          : calculateBestAvgSpeed(this.points, 2, 0, newSpeedUnit)[0];
        this.bestSpeedTenSeconds = this.bestSpeedTenSeconds
          ? ConvertUtil.convertSpeed(
              ConvertUtil.convertSpeedInKM(this.bestSpeedTenSeconds, this.speedUnit),
              newSpeedUnit
            )
          : calculateBestAvgSpeed(this.points, 10, 0, newSpeedUnit)[0];
        this.bestSpeedTwentySeconds = this.bestSpeedTwentySeconds
          ? ConvertUtil.convertSpeed(
              ConvertUtil.convertSpeedInKM(this.bestSpeedTwentySeconds, this.speedUnit),
              newSpeedUnit
            )
          : calculateBestAvgSpeed(this.points, 20, 0, newSpeedUnit)[0];
        this.bestSpeedHundredMeter = this.bestSpeedHundredMeter
          ? ConvertUtil.convertSpeed(
              ConvertUtil.convertSpeedInKM(this.bestSpeedHundredMeter, this.speedUnit),
              newSpeedUnit
            )
          : calculateBestAvgSpeed(this.points, 0, 100, newSpeedUnit)[0];
        this.bestSpeed250Meter = this.bestSpeed250Meter
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed250Meter, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 250, newSpeedUnit)[0];
        this.bestSpeedFiveHundredMeter = this.bestSpeedFiveHundredMeter
          ? ConvertUtil.convertSpeed(
              ConvertUtil.convertSpeedInKM(this.bestSpeedFiveHundredMeter, this.speedUnit),
              newSpeedUnit
            )
          : calculateBestAvgSpeed(this.points, 0, 500, newSpeedUnit)[0];
        this.bestSpeedOneKM = this.bestSpeedOneKM
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeedOneKM, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 1000, newSpeedUnit)[0];
        this.bestSpeedOneNM = this.bestSpeedOneNM
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeedOneNM, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 1852, newSpeedUnit)[0];
        this.bestSpeedOneMi = this.bestSpeedOneMi
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeedOneMi, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 1609, newSpeedUnit)[0];
        this.bestSpeed5KM = this.bestSpeed5KM
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed5KM, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 5000, newSpeedUnit)[0];
        this.bestSpeed10KM = this.bestSpeed10KM
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed10KM, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 10000, newSpeedUnit)[0];
        this.bestSpeed5Mi = this.bestSpeed5Mi
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed5Mi, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 8045, newSpeedUnit)[0];
        this.bestSpeed10Mi = this.bestSpeed10Mi
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed10Mi, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 0, 16090, newSpeedUnit)[0];
        this.bestSpeed1hour = this.bestSpeed1hour
          ? ConvertUtil.convertSpeed(ConvertUtil.convertSpeedInKM(this.bestSpeed1hour, this.speedUnit), newSpeedUnit)
          : calculateBestAvgSpeed(this.points, 3600, 0, newSpeedUnit)[0];
      }

      this.speedUnit = newSpeedUnit;
    }

    this.wasPreview = preview;
  }

  getWaterSportName(waterSportId: any = undefined): string {
    if (isNil(waterSportId) || waterSportId === "") {
      waterSportId = this.waterSportId;
    }

    if (waterSportId === null || waterSportId === undefined) return "";

    const sportType = WATERSPORT_OPTIONS.find((s) => s.value === waterSportId);
    if (sportType) {
      return sportType.label;
    } else {
      return "";
    }
  }

  getWaterSportIcon(waterSportId: any = undefined): string | null {
    if (isNil(waterSportId) || waterSportId === "" || waterSportId === -1) {
      waterSportId = this.waterSportId;
    }
    if (isNil(waterSportId) || waterSportId === "") return null;

    return `/assets/icons/sports/${waterSportId}.png`;
  }

  mapWaterSportIdToGPSSpeedsurfingSport(): number | null {
    /**
     * GPS-Speedsurfing uses different sport ids than the ones we use, their mapping is as follows:
     * 1 ice 2 foil 3 windsurf 4 kitesurf 5 sail 6 wing
     */
    switch (this.waterSportId) {
      case 26:
        return 1;
      case 10:
        return 2;
      case 2:
        return 3;
      case 4:
      case 14:
      case 25:
      case 23:
      case 24:
        return 4;
      case 1:
        return 5;
      case 18:
        return 6;
      default:
        return null;
    }
  }

  mapWaterSportIdToStravaSport(): string {
    /**
     * Strava's upload endpoint accepts an `activity_type` from a fixed list. We map our
     * watersport ids to the closest Strava type, mirroring the historical iOS mapping
     * (stravaTypeSport). Defaults to Windsurf for anything unmapped.
     */
    switch (this.waterSportId) {
      case 1:
      case 26:
        return "Sail";
      case 3:
        return "StandUpPaddling";
      case 4:
      case 14:
      case 18:
      case 23:
      case 24:
      case 25:
        return "KiteSurf";
      case 5:
      case 9:
      case 17:
      case 22:
        return "Kayaking";
      case 6:
      case 7:
        return "Workout";
      case 8:
      case 11:
      case 12:
      case 15:
      case 16:
      case 21:
        return "Surfing";
      case 13:
        return "Rowing";
      case 19:
      case 20:
        return "Canoeing";
      case 0:
      case 2:
      case 10:
      default:
        return "Windsurf";
    }
  }

  mapGarminWaterSportToWaterSportId(garminWaterSport: string): number | null {
    /**
     * Garmin Connect uses different sport ids than the ones we use, their mapping is as follows:
     * BOATING_V2, BOATING
     * KAYAKING_V2, KAYAKING
     * KITEBOARDING_V2, KITEBOARDING
     * OFFSHORE_GRINDING_V2, OFFSHORE_GRINDING
     * ONSHORE_GRINDING_V2, ONSHORE_GRINDING
     * PADDLING_V2, PADDLING
     * STAND_UP_PADDLEBOARDING_V2, STAND_UP_PADDLEBOARDING
     * ROWING_V2, ROWING
     * SAILING_V2, SAILING
     * SURFING_V2, SURFING
     * WAKEBOARDING_V2, WAKEBOARDING
     * WATERSKIING
     * WHITEWATER_RAFTING_V2, WHITEWATER_RAFTING
     * WINDSURFING_V2, WINDSURFING
     */
    switch (garminWaterSport) {
      case "BOATING_V2":
      case "BOATING":
        return 6;
      case "KAYAKING_V2":
      case "KAYAKING":
        return 5;
      case "KITEBOARDING_V2":
      case "KITEBOARDING":
        return 4;
      case "OFFSHORE_GRINDING_V2":
      case "OFFSHORE_GRINDING":
      case "ONSHORE_GRINDING_V2":
      case "ONSHORE_GRINDING":
      case "SAILING_V2":
      case "SAILING":
        return 1;
      case "PADDLING_V2":
      case "PADDLING":
      case "STAND_UP_PADDLEBOARDING_V2":
      case "STAND_UP_PADDLEBOARDING":
        return 3;
      case "ROWING_V2":
      case "ROWING":
        return 13;
      case "SURFING_V2":
      case "SURFING":
        return 8;
      case "WAKEBOARDING_V2":
      case "WAKEBOARDING":
        return 12;
      case "WATERSKIING":
        return 11;
      case "WHITEWATER_RAFTING_V2":
      case "WHITEWATER_RAFTING":
        return 5;
      case "WINDSURFING_V2":
      case "WINDSURFING":
        return 2;
      case "OTHER":
      case "WATER_SPORTS":
        return 0;
      default:
        return null;
    }
  }

  getTypeName(): string {
    if (this.typeId === null || this.typeId === undefined) return "";

    const trainingType = ACTIVITY_FOR_OPTIONS.find((s) => s.value === this.typeId);
    if (trainingType) {
      return trainingType.label;
    } else {
      return "";
    }
  }

  getHeading(): string {
    if (this.bearing) {
      return this.bearing.toString().replace("�", "\xB0");
    }

    return "";
  }

  getSecondaryName(): string {
    const hours = this.originalDate.getHours();
    const greeting = GreetUtil.getGreetingByHours(hours);
    return `${this.waterSportName}, ${greeting}`;
  }

  getLocation(): string {
    return `${this.country} ${this.city}`;
  }

  getLocationReversed(): string {
    return `${this.city} - ${this.country}`;
  }

  getGroupByDaily(): string {
    return this.dateInDDMMYYYY;
  }

  getGroupByWeekly(): string {
    const year = this.originalDate.getFullYear();
    return `W${weekNumber(this.date)} - ${year}`;
  }

  getGroupByMonthly(): string {
    const month = this.originalDate.getMonth();
    const year = this.originalDate.getFullYear();
    return `${DateTimeUtil.monthSortNames[parseInt(month as any)]}/${year}`;
  }

  getGroupByYearly(): number {
    return this.originalDate.getFullYear();
  }

  getPointsFromDbPoints(): any[] {
    if (!this.dbPoints || this.dbPoints.length === 0) {
      return [];
    }

    const filteredDbPoints = ArrayUtil.removeNullAndUndefinedValue(this.dbPoints);

    const sortedDbPoints = sortDbPoints(filteredDbPoints);

    // Calculate bearing from GPS coordinates if missing (especially important for Android activities)
    const dbPointsWithBearing = sortedDbPoints.map((dbPoint, index) => {
      // If bearing is missing or invalid, calculate it from previous point
      if (
        (dbPoint.r === null || dbPoint.r === undefined || dbPoint.r < 0) &&
        index > 0 &&
        sortedDbPoints[index - 1].l &&
        sortedDbPoints[index - 1].n &&
        dbPoint.l &&
        dbPoint.n
      ) {
        const prevPoint = sortedDbPoints[index - 1];
        const calculatedBearing = DirectionUtil.getBearingValue(prevPoint.l, prevPoint.n, dbPoint.l, dbPoint.n);
        return {...dbPoint, r: calculatedBearing >= 0 ? calculatedBearing : -1};
      }
      return dbPoint;
    });

    let points = dbPointsWithBearing.map((dbPoint) => new Point(dbPoint));

    points = calculateTimeAndDistanceOfPoints(points);

    return points;
  }

  getHeartRatesFromPoints(points: any[]): number[] {
    return points.map((p) => {
      return p.heartRate;
    });
  }

  getCoordsFromPoints(points: any[]): Coordinate[] {
    return points.map((p) => {
      return {latitude: p.latitude, longitude: p.longitude};
    });
  }

  getFirstCoordFromActivityOrPoints(): any {
    if (this.latitude && this.longitude) {
      return {latitude: this.latitude, longitude: this.longitude};
    }

    return this.points.find((p) => p.latitude && p.longitude);
  }

  getCalories(): number {
    if (this.points.length <= 0) {
      return 0;
    }

    const lastPoint = this.points[this.points.length - 1];

    return lastPoint.calories;
  }

  async getWindInformation(infos?: any): Promise<any> {
    /**
     * Get the wind information of the activity from WeatherMapkit Apple API
     */
    let originalDateInUTC;
    let durationInSeconds;
    let point;
    let shouldReturn = false;
    if (infos) {
      originalDateInUTC = infos.originalDateInUTC;
      durationInSeconds = infos.durationInSeconds;
      point = infos.point;
      shouldReturn = true;
    } else {
      originalDateInUTC = this.originalDateInUTC;
      durationInSeconds = this.durationInSeconds;

      // Find first point with latitude and longitude
      point = this.points.find((p) => p.latitude && p.longitude);
    }
    const token = process.env.APPLE_WEATHER_MAPKIT_TOKEN;
    const startDateTimestamp = originalDateInUTC.getTime();
    const startDate = DateTimeUtil.convertDateToString(
      new Date(startDateTimestamp),
      DATE_FORMATS.YYYYMMDDTHHMMSSZ,
      true
    );
    let endDateTimeStamp = startDateTimestamp + durationInSeconds * 1000;

    // Check if the end date is more than 24 hours than the start date
    if (endDateTimeStamp - startDateTimestamp > 24 * 60 * 60 * 1000) {
      endDateTimeStamp = startDateTimestamp + 24 * 60 * 60 * 1000;
    }

    const endDatePlusOneHour = DateTimeUtil.convertDateToString(
      new Date(endDateTimeStamp + 60 * 60 * 1000),
      DATE_FORMATS.YYYYMMDDTHHMMSSZ,
      true
    );
    const url = `https://weatherkit.apple.com/api/v1/weather/en_US/${point?.latitude}/${point?.longitude}?dataSets=forecastHourly&currentAsOf=${startDate}&hourlyStart=${startDate}&hourlyEnd=${endDatePlusOneHour}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).catch((error) => {
      throw error;
    });
    if (!response.ok) {
      console.error("Failed to fetch weather data", {url, response: await response.text()});
      throw new Error("Failed to fetch weather data");
    }

    const jsonResponse = await response.json();
    const forecastWeather = jsonResponse.forecastHourly?.hours;

    if (forecastWeather?.length > 0) {
      // Find the hour just before the start of the activity and the hour just after the end of the activity
      const forecastHours = [];
      for (const forecastHour of forecastWeather) {
        const forecastHourTimestamp = new Date(forecastHour.forecastStart).getTime();
        forecastHour.cartesionWindAngle = ConvertUtil.convertDegreeToCartesian(forecastHour.windDirection);
        if (forecastHourTimestamp < startDateTimestamp || !forecastHours.length) {
          forecastHours.push(forecastHour);
        }
        if (forecastHourTimestamp > startDateTimestamp && forecastHourTimestamp < endDateTimeStamp) {
          forecastHours.push(forecastHour);
        }
        if (forecastHourTimestamp > endDateTimeStamp) {
          forecastHours.push(forecastHour);
          break;
        }
      }

      // Calculate average coordinates for the wind direction
      const avgX = forecastHours.reduce((sum, point) => sum + point.cartesionWindAngle.x, 0) / forecastHours.length;
      const avgY = forecastHours.reduce((sum, point) => sum + point.cartesionWindAngle.y, 0) / forecastHours.length;
      let avgWindAngle = ConvertUtil.convertCartesianToDegree(avgX, avgY);
      avgWindAngle = (avgWindAngle + 360) % 360;

      this.windAngle = Math.floor(avgWindAngle);
      this.windDirection = DirectionUtil.getDirectionByBearing(this.windAngle, true);
      this.windSpeedInKnots = ConvertUtil.convertSpeed(
        forecastHours.reduce((a, b) => a + b.windSpeed, 0) / forecastHours.length,
        SETTING_SPEED_UNITS.knots
      );
      // See note in the main constructor: SQL is the source of truth, so
      // we don't pre-run the local recognizer here either. useActivityManeuvers
      // will refetch / recalc and swap in the authoritative service.
      this.tacksAndJibesService = EMPTY_TACKS_AND_JIBES_SERVICE;

      if (this.points && this.points.length > 0) {
        const pointsWithVMG = calculateVMGForPoints(this.points, this.windAngle);
        this.points = pointsWithVMG;
        this.averageVMGInKM = calculateAverageVMG(this.points, this.windAngle);
      }

      if (shouldReturn) {
        return {
          windAngle: this.windAngle,
          windDirection: this.windDirection,
          windSpeedInKnots: this.windSpeedInKnots
        };
      }
    }
  }

  async getAppleMapsToken(isNativePlatform: boolean = false): Promise<any> {
    const token = await TokenService.fetchMapkitJsToken(!isNativePlatform);
    const tokenUrl = "https://maps-api.apple.com/v1/token";
    const responseToken = await fetch(tokenUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).catch((error) => {
      console.log("Error getting Apple Maps token", error);
      throw error;
    });
    const responseTokenJson = await responseToken.json();
    return responseTokenJson.accessToken;
  }

  async getLocationInformation(
    lat: number | null = null,
    long: number | null = null,
    accessToken: string | null = null,
    isNativePlatform: boolean = false
  ): Promise<void> {
    /**
     * Use the Apple Mapkit API to get the location information of the activity (city, country)
     */
    if (!accessToken) {
      accessToken = await this.getAppleMapsToken(isNativePlatform);
    }

    const url = `https://maps-api.apple.com/v1/reverseGeocode?loc=${lat || this.points[0].latitude},${long || this.points[0].longitude}&lang=en-US`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).catch((error) => {
      throw error;
    });
    const jsonResponse = await response.json();

    if (jsonResponse?.results?.length > 0) {
      const result = jsonResponse.results[0];
      this.city = result.structuredAddress.locality || result.name;
      this.country = result.country;
    }
  }

  convertActivityToActivityDb(userUid: string): any {
    return {
      ID: this.id,
      dS: this.startDateS || DateTimeUtil.getUnixTimestampByDate(this.originalDateInUTC),
      dSE: DateTimeUtil.convertDateToString(this.originalDateInUTC, DATE_FORMATS.YYYYMMDDTHHMMSSZ),
      nm: this.name,
      fe: this.feelingId ? toFeelingId(this.feelingId) : null,
      tt: this.typeId || ACTIVITY_DEFAULT_VALUES.typeId,
      tp: this.waterSportId,
      brg: this.bearing,
      aS: this.from || "",
      aE: this.to || "",
      eq: this.equipment1 || "",
      eq2: this.equipment2 || "",
      eq3: this.equipment3 || "",
      ci: this.city,
      co: this.country,
      ps: this.pausedTimeInSeconds,
      wA: this.windAngle,
      wD: this.windDirection,
      wS: this.windSpeedInKnots,
      sT: this.durationInSeconds,
      dT: this.totalDistanceInKm,
      dTL: this.linearDistance,
      sA: this.averageSpeedInKM,
      sM: this.maxSpeedInKM || 0,
      bs5: this.bestSpeedFiveSecondsKM,
      lt: this.latitude,
      lg: this.longitude,
      dsc: this.comment,
      isDel: this.isDeleted || false,
      sv: this.isSaved || true,
      fnt: this.fnt,
      fntM: this.fntM,
      gId: ThirdPartyIdUtil.normalise(this.garminActivityId),
      sId: ThirdPartyIdUtil.normalise(this.suuntoWorkoutKey),
      cId: ThirdPartyIdUtil.normalise(this.corosWorkoutKey),
      sort: 0,
      str: 0,
      tck: 0,
      usr: userUid,
      lmd: DateTimeUtil.getUnixTimestampByDate(new Date()),
      devM: this.deviceName
    };
  }

  convertActivityToActivityDbAndPointsDb(userUid: string): {activityDb: any; pointsDb: any[]} {
    /**
     * Convert current activity to a json object that can be stored in the database containing the activity and the points in the format that the database expects
     * @return {{points: *, activity: *}}
     */
    let activityDb = this.convertActivityToActivityDb(userUid);

    const pointsDb = this.points.map((point, index) => convertPointToPointDb(point, index));

    // Remove null and undefined values from activityDb and pointsDb
    activityDb = cleanActivityDb(activityDb);
    pointsDb.forEach((point) => cleanPointDb(point));

    return {activityDb, pointsDb};
  }
}

export const cleanActivityDb = (activityDb: any): any => {
  /**
   * Remove null and undefined values from the activity object
   */
  Object.keys(cloneDeep(activityDb)).forEach((key) => isNil(activityDb[key]) && delete activityDb[key]);
  return activityDb;
};

export const cleanPointDb = (point: any): any => {
  /**
   * Remove null and undefined values from the point object
   * @param point: Point object
   * @return: Point object
   */
  Object.keys(cloneDeep(point)).forEach((key) => isNil(point[key]) && delete point[key]);
  return point;
};

export const convertPointToPointDb = (point: any, index: number): any => {
  return {
    a: point.accuracy,
    c: point.calories ?? 0,
    h: point.heartRate ?? 0,
    l: point.latitude,
    n: point.longitude,
    r: point.bearing ?? "",
    p: point.pauseInSecond ?? 0,
    s: point.speedInMPS || 0,
    e: point.elevationInMeter,
    t: point.unixTimestamp,
    m: point.id,
    i: index.toString()
  };
};

const calculateHeartRate = (points: any[]): MinAvgMaxHeartRate => {
  let min = 1000;
  let max = 0;
  let avg = 0;
  let sum = 0;

  let count = 0;
  let heartRateCount = 0;

  if (points.length > 0) {
    while (count < points.length) {
      if (points[count].heartRate > 0) {
        if (min > points[count].heartRate) {
          min = points[count].heartRate;
        }
        if (max < points[count].heartRate) {
          max = points[count].heartRate;
        }
      }
      count += 1;
    }

    count = 0;

    while (count < points.length) {
      if (points[count].heartRate > 0) {
        if (points[count].heartRate > min) {
          heartRateCount += 1;
          sum += points[count].heartRate;
        }
      }
      count += 1;
    }

    if (heartRateCount > 0) {
      avg = sum / heartRateCount;
    } else {
      avg = (min + max) / 2;
    }
  } else {
    min = 0;
  }

  if (min > 300) {
    min = 0;
  }
  if (max > 300) {
    max = 0;
  }
  if (avg > 300) {
    avg = 0;
  }

  return {
    min: Math.round(min),
    avg: Math.round(avg),
    max: Math.round(max)
  };
};

// Helper function to convert points to WSSpeedCalculator format
const convertPointsToLocations = (points: any[]): any[] => {
  return points.map((point) => ({
    latitude: point.latitude,
    longitude: point.longitude,
    altitude: point.altitude || 0,
    speed: point.speedInMPS || 0,
    timestamp: point.unixTimestamp,
    horizontalAccuracy: point.horizontalAccuracy || 0,
    verticalAccuracy: point.verticalAccuracy || 0,
    course: point.course || 0
  }));
};

export const calculateBestAvgSpeed = (
  points: any[],
  period: number = 0,
  meters: number = 0,
  speedUnit?: string | null,
  returnTime: boolean = false
): any => {
  if (!points || points.length < 2) {
    if (returnTime) return null;
    return [0, 0, 0, 0.0];
  }

  const locations = convertPointsToLocations(points);
  let window;

  if (period > 0 && meters === 0) {
    window = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, period, `period_${period}`);
  } else if (meters > 0 && period === 0) {
    window = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.DISTANCE, meters, `distance_${meters}`);
  } else {
    if (returnTime) return null;
    return [0, 0, 0, 0.0];
  }

  const result = WSSpeedCalculator.getBestAverageSpeed(locations, window);

  if (!result) {
    if (returnTime) return null;
    return [0, 0, 0, 0.0];
  }

  let duration = 0;
  if (result.range && result.range.length === 2) {
    const startIdx = result.range[0];
    const endIdx = result.range[1];
    if (startIdx >= 0 && endIdx < points.length && endIdx > startIdx) {
      duration = points[endIdx].unixTimestamp - points[startIdx].unixTimestamp;
    }
  }

  if (returnTime) {
    return duration > 0 ? duration : null;
  }

  const speedInKmh = ConvertUtil.convertMPSToKMH(result.speed);
  const convertedSpeed = ConvertUtil.convertSpeed(speedInKmh, speedUnit);

  return [convertedSpeed, result.range ? result.range[0] : 0, result.range ? result.range[1] : 0, duration];
};

/**
 * Use calculateBestAvgSpeed method logic.
 * Add an array to store each best avg speed.
 * Take five best speeds from array.
 *
 * @param {Array<Point>} points - Point array of activity
 * @param {string} speedUnit - Speed unit
 *
 * @return {Array<number>} array that contain five best ten second speeds
 */
export const findFiveBestTenSecondSpeedAsync = (points: any[], speedUnit: string): number[] => {
  const speedAvgArr = [];

  let speed = 0;
  let bottom = 0;
  let up = 0;

  const listOfPoints = cloneDeep(points);

  for (let i = 0; i < 5; i++) {
    if (listOfPoints.length === 0) {
      break;
    }

    const ret = calculateBestAvgSpeed(listOfPoints, 10, 0, SETTING_SPEED_UNITS.km_slash_h);
    speed = ret[0];
    bottom = ret[1];
    up = ret[2];

    speedAvgArr.push(speed);

    // Remove the segment from bottom to up (inclusive) if valid
    if (up < listOfPoints.length && bottom < listOfPoints.length && up > bottom && up >= 0 && bottom >= 0) {
      listOfPoints.splice(bottom, up - bottom + 1);
    } else if (bottom < listOfPoints.length && bottom >= 0) {
      listOfPoints.splice(bottom, 1);
    }
  }

  if (speedAvgArr.length < 5) {
    return [0, 0, 0, 0, 0];
  }

  const result = [];
  for (let index = 0; index < 5; index++) {
    result.push(ConvertUtil.convertSpeed(speedAvgArr[index], speedUnit));
  }

  return result;
};

/**
 * Use calculateBestAvgSpeed method logic.
 * @param {Array<Point>} points - Point array of activity
 * @param {string} speedUnit - Speed unit
 * @return {number} speed
 */
export const calculateAvgSpeed = (points: any[], speedUnit: string = SETTING_SPEED_UNITS.km_slash_h): number => {
  if (!points || points.length < 2) {
    return 0;
  }
  // Use calculateBestAvgSpeed for the full activity duration
  const start = points[0].unixTimestamp;
  const end = points[points.length - 1].unixTimestamp;
  const duration = end - start - 0.005;
  if (duration <= 0) {
    return 0;
  }
  // period = duration, meters = 0 (use time window)
  const [avgSpeed] = calculateBestAvgSpeed(points, duration, 0, speedUnit);
  return avgSpeed;
};

export const getFeelingId = (value: any): string => {
  let output;

  switch (value) {
    case "":
      output = "straight";
      break;
    case 0:
      output = "straight";
      break;
    case 1:
      output = "crying";
      break;
    case 2:
      output = "sad";
      break;
    case 3:
      output = "straight";
      break;
    case 4:
      output = "smiley";
      break;
    case 5:
      output = "happy";
      break;
    default:
      throw new Error(`invalid feeling id: ${value}`);
  }

  return output;
};

const toFeelingId = (value: any): number => {
  let output;

  switch (value) {
    case "straight":
      output = 0;
      break;
    case "crying":
      output = 1;
      break;
    case "sad":
      output = 2;
      break;
    case "smiley":
      output = 4;
      break;
    case "happy":
      output = 5;
      break;
    default:
      throw new Error(`invalid feeling value: ${value}`);
  }

  return output;
};

export const convertValueToFeelingId = (value: any): number => {
  let output;

  switch (value) {
    case "straight":
      output = 0;
      break;
    case "crying":
      output = 1;
      break;
    case "sad":
      output = 2;
      break;
    case "smiley":
      output = 4;
      break;
    case "happy":
      output = 5;
      break;
    default:
      throw new Error(`invalid feeling value: ${value}`);
  }

  return output;
};

export const canShowJibeAndTack = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  return (
    waterSportId === 0 ||
    waterSportId === 1 ||
    waterSportId === 2 ||
    waterSportId === 4 ||
    waterSportId === 10 ||
    waterSportId === 14 ||
    waterSportId === 16 ||
    waterSportId === 18 ||
    waterSportId === 23 ||
    waterSportId === 24 ||
    waterSportId === 25 ||
    waterSportId === 26 ||
    waterSportId === 32
  );
};

export const canShowAlpha = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  // Show for Windsurf (waterSportId === 2) and Windfoil (waterSportId === 10)
  return waterSportId === 2 || waterSportId === 10;
};

export const canShowMissedTacksAndJibes = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  return (
    waterSportId === 2 ||
    waterSportId === 4 ||
    waterSportId === 10 ||
    waterSportId === 14 ||
    waterSportId === 16 ||
    waterSportId === 18 ||
    waterSportId === 23 ||
    waterSportId === 24 ||
    waterSportId === 25 ||
    waterSportId === 32
  );
};

export const canShowAngle = (waterSportId: any): boolean => {
  if (!waterSportId) {
    return false;
  }

  return isSailingSport(waterSportId);
};

/**
 * Check if a sport is wind-related and can show polar chart
 * Polar charts show boat/board speed vs. True Wind Angle (TWA)
 * Useful for all wind-powered sports
 *
 * @param {number} waterSportId - Water sport ID
 * @return {boolean} True if the sport is wind-related and can show polar chart
 */
export const canShowPolarChart = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  // Wind-related sports that use wind angles:
  // 1: Sailing, 2: Windsurf, 4: Kite, 10: Windfoil, 14: Kitefoil,
  // 18: Wingfoil, 23: Tiki, 24: Strapless, 25: Snow Kite, 26: Ice Sailing, 32: Parawing
  return (
    waterSportId === 1 ||
    waterSportId === 2 ||
    waterSportId === 4 ||
    waterSportId === 10 ||
    waterSportId === 14 ||
    waterSportId === 18 ||
    waterSportId === 23 ||
    waterSportId === 24 ||
    waterSportId === 25 ||
    waterSportId === 26 ||
    waterSportId === 32
  );
};

export const canShowFoiling = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  return (
    waterSportId === 1 ||
    waterSportId === 2 ||
    waterSportId === 4 ||
    waterSportId === 10 ||
    waterSportId === 14 ||
    waterSportId === 15 ||
    waterSportId === 16 ||
    waterSportId === 18 ||
    waterSportId === 23 ||
    waterSportId === 24 ||
    waterSportId === 28 ||
    waterSportId === 30 ||
    waterSportId === 31 ||
    waterSportId === 32 ||
    waterSportId === 33
  );
};

export const canShowExtraDistance = (waterSportId: any): boolean => {
  if (waterSportId === null || waterSportId === undefined || waterSportId === "") {
    return false;
  }

  return waterSportId === 28 || waterSportId === 30 || waterSportId === 31 || waterSportId === 32;
};

/**
 * Check each point of activity and if a point have speed less then specified in parameter then add the point to output array.
 *
 * @param {Array<Point>} points - Point array of activity
 * @param {number} pauseSpeedInKMH - Pause speed in kilometer per hour
 *
 * @return {Array<Point>} Point array where speed is less then specified in param.
 */
export const getPausePoints = (points: any[], pauseSpeedInKMH: number = 1.852): any[] => {
  const pausePoints = [];

  if (!points || points.length === 0) {
    return pausePoints;
  }

  let point;
  let count = 0;
  while (count < points.length) {
    point = points[count];
    if (point.speedInKMH <= pauseSpeedInKMH) {
      pausePoints.push(point);
    }
    count++;
  }

  return pausePoints;
};

/**
 * Loop through each point of activity.
 * If speed is less then specified in param, then run inner loop until next point speed is less then specified in param.
 * The inner loop contain points of singal foiling track.
 * After inner loop finish add the foiling track to foiling track array.
 *
 * @param {Array<Point>} points - Point array of activity
 * @param {number} foilingSpeedInKM - foiling speed in kilometer
 *
 * @return {Array<Array<Point>>} array of array, an array contain points where speed is less than specified in param.
 */
export const getFoilingTracksAsync = (points: any[], foilingSpeedInKM: number): any[][] => {
  let foilingTrack = [];
  const foilingTracks = [];

  if (!points || points.length === 0) {
    return foilingTracks;
  }

  let isFoilingPoint = false;
  let point;
  let count = 0;

  while (count < points.length - 1) {
    point = points[count];
    isFoilingPoint = point.speedInKMH >= foilingSpeedInKM;

    if (isFoilingPoint) {
      while (count < points.length - 1 && isFoilingPoint) {
        foilingTrack.push(point);
        count++;
        point = points[count];
        isFoilingPoint = point.speedInKMH >= foilingSpeedInKM;
      }
      foilingTracks.push(foilingTrack);
      foilingTrack = [];
    } else {
      count++;
    }
  }

  return foilingTracks;
};

/**
 * Helper function to determine if a section is sailing downwind
 * Analyzes multiple consecutive points to determine if boat is moving in wind direction
 *
 * @param {Array<Point>} pointWindow - Array of 3-5 consecutive points
 * @param {number} windAngle - Wind direction in degrees (direction wind is coming FROM)
 * @return {boolean} True if section is sailing downwind
 */
const isDownwindSection = (pointWindow: any[], windAngle: number): boolean => {
  if (!pointWindow || pointWindow.length < 3) {
    return false;
  }

  // Wind angle is where wind comes FROM, so wind blows TO the opposite direction
  const windBlowsToDirection = (windAngle + 180) % 360;

  let downwindCount = 0;
  let validCount = 0;

  for (let i = 0; i < pointWindow.length; i++) {
    const point = pointWindow[i];

    // Skip points with invalid bearing
    if (point.bearing < 0) {
      continue;
    }

    validCount++;

    // Calculate TWA (True Wind Angle)
    const twa = calculateTWA(point.bearing, windAngle);

    // Calculate angle difference between boat bearing and wind blowing direction
    const bearingDiff = Math.abs(point.bearing - windBlowsToDirection);
    const normalizedBearingDiff = bearingDiff > 180 ? 360 - bearingDiff : bearingDiff;

    // Consider downwind if:
    // 1. TWA >= 90 (sailing more than 90° from wind)
    // 2. Boat is heading within 60° of the direction wind is blowing
    // This catches broad reach and running downwind
    if (twa !== null && twa >= 90 && normalizedBearingDiff <= 60) {
      downwindCount++;
    }
  }

  // Consider section downwind if at least 2/3 of valid points are downwind
  return validCount > 0 && downwindCount / validCount >= 0.67;
};

/**
 * Helper function to get foiling tracks with wind-aware speed thresholds
 * Uses lower threshold for downwind sections (70% of normal threshold)
 * Checks sections (multiple points) to better detect downwind sections
 *
 * @param {Array<Point>} points - Point array of activity
 * @param {number} foilingSpeedInKM - foiling speed in kilometer
 * @param {number} windAngle - Wind direction in degrees
 * @return {Array<Array<Point>>} array of array, an array contain points where speed meets threshold
 */
export const getFoilingTracksWithWindAware = (points: any[], foilingSpeedInKM: number, windAngle: number): any[][] => {
  if (!points || points.length === 0 || windAngle < 0) {
    return getFoilingTracksAsync(points, foilingSpeedInKM);
  }

  let foilingTrack = [];
  const foilingTracks = [];
  let point;
  let count = 0;

  // Downwind threshold is 70% of normal threshold
  const downwindThreshold = foilingSpeedInKM * 0.7;
  // Window size for analyzing if section is downwind
  const windowSize = 4;

  while (count < points.length - 1) {
    point = points[count];

    // Check if point has valid bearing
    if (point.bearing < 0) {
      count++;
      continue;
    }

    // Check if we should use downwind threshold by analyzing a window of points
    const startIdx = Math.max(0, count - Math.floor(windowSize / 2));
    const endIdx = Math.min(points.length, count + Math.ceil(windowSize / 2));
    const pointWindow = points.slice(startIdx, endIdx);
    const useDownwindThreshold = isDownwindSection(pointWindow, windAngle);

    // Try both thresholds - use downwind threshold if section is downwind
    const threshold = useDownwindThreshold ? downwindThreshold : foilingSpeedInKM;
    const isFoilingPoint = point.speedInKMH >= threshold;

    if (isFoilingPoint) {
      // Start a new foiling track
      foilingTrack = [point];
      count++;

      // Continue adding points to this track as long as speed meets threshold
      while (count < points.length - 1) {
        point = points[count];

        // Skip points with invalid bearing
        if (point.bearing < 0) {
          count++;
          continue;
        }

        // Re-check if current window is downwind
        const startIdx = Math.max(0, count - Math.floor(windowSize / 2));
        const endIdx = Math.min(points.length, count + Math.ceil(windowSize / 2));
        const pointWindow = points.slice(startIdx, endIdx);
        const useDownwindThreshold = isDownwindSection(pointWindow, windAngle);

        // Use appropriate threshold based on wind analysis
        const pointThreshold = useDownwindThreshold ? downwindThreshold : foilingSpeedInKM;
        const pointIsFoiling = point.speedInKMH >= pointThreshold;

        if (pointIsFoiling) {
          foilingTrack.push(point);
          count++;
        } else {
          // End of foiling track
          break;
        }
      }

      // Only add track if it has at least 2 points
      if (foilingTrack.length > 1) {
        foilingTracks.push(foilingTrack);
      }
      foilingTrack = [];
    } else {
      count++;
    }
  }

  return foilingTracks;
};

export const createActivityFromFitFile = async (
  fitFileBuffer: any,
  uid?: string,
  forcedWatersportId?: any
): Promise<Activity | null | undefined> => {
  // Parse the .fit file into structured data
  const parsedData = await parseFitFile(fitFileBuffer, uid, forcedWatersportId);

  if (!parsedData || !parsedData.records || parsedData.records.length === 0) {
    console.warn(`Invalid or empty fit file${uid ? ` for user ${uid}` : ""}:`, {
      "parsedData.records.length": parsedData?.records?.length || 0
    });
    throw new Error(`Invalid or empty fit file${uid ? ` for user ${uid}` : ""}`);
  }

  if (isNil(parsedData.sportId)) {
    return undefined;
  }

  if (!parsedData.records || parsedData.records.length === 0) {
    return null;
  }

  // Get the equipments corresponding to the sport type and assign the first three to the activity
  const equipmentFactory: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.equip);
  let equipments = await equipmentFactory.getAllEquipsByUser(
    uid,
    null,
    {
      field: "sport",
      value: parsedData.sportId
    },
    3,
    false,
    undefined
  );
  equipments = equipments
    ? Object.values(equipments)?.filter((equipment: any) => equipment.sport === parsedData.sportId)
    : [];
  parsedData.equipment1 = equipments?.[0]?.ID;
  parsedData.equipment2 = equipments?.[1]?.ID;
  parsedData.equipment3 = equipments?.[2]?.ID;

  const navigation = {
    ID: null,
    dS: parsedData.startTimestamp,
    nm: parsedData.sessionName || "",
    fe: parsedData.feeling || 0,
    fnt: "FIT",
    tt: parsedData.typeId || 0,
    tp: parsedData.sportId || 0,
    brg: parsedData.bearing || 0,
    aS: parsedData.from || "",
    aE: parsedData.to || "",
    eq: parsedData.equipment1 || "",
    eq2: parsedData.equipment2 || "",
    eq3: parsedData.equipment3 || "",
    ci: parsedData.city || "",
    co: parsedData.country || "",
    ps: parsedData.pausedTime || 0,
    wA: parsedData.windAngle || 0,
    wD: parsedData.windDirection || "N",
    wS: parsedData.windSpeed || 0,
    sT: parsedData.totalTime || 0,
    dT: parsedData.totalDistance || 0,
    sA: parsedData.avgSpeed || 0,
    sM: parsedData.maxSpeed || 0,
    lt: parsedData.latitude || 0,
    lg: parsedData.longitude || 0,
    dsc: parsedData.comment || "",
    calories: parsedData.calories || 0,
    lmd: Date.now(),
    gId: ThirdPartyIdUtil.normalise(parsedData.garminActivityId),
    sId: ThirdPartyIdUtil.normalise(parsedData.suuntoWorkoutKey),
    cId: ThirdPartyIdUtil.normalise(parsedData.corosWorkoutKey),
    devM: parsedData.deviceName
  };

  // Deduplicate records by latitude, longitude and timestamp before building points
  const seenKeys = new Set();
  const uniqueRecords = parsedData.records.filter((rec) => {
    const lat = rec.latitude ?? 0;
    const lon = rec.longitude ?? 0;
    const t = rec.timestamp ?? 0;
    const key = `${t}-${Number(lat).toFixed(7)}-${Number(lon).toFixed(7)}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  if (uniqueRecords.length < parsedData.records.length) {
    console.log(
      `Deduplicated FIT points: ${uniqueRecords.length} unique from ${parsedData.records.length} (removed ${parsedData.records.length - uniqueRecords.length} duplicates)`
    );
  }

  const dbPoints = uniqueRecords.map((rec, idx) => ({
    m: `pt-${idx}`,
    a: rec.accuracy || 0,
    c: rec.calories || 0,
    h: rec.heartRate || 0,
    l: rec.latitude,
    n: rec.longitude,
    r: rec.bearing || 0,
    p: rec.pauseTime || 0,
    s: rec.speed || 0,
    e: rec.elevation || 0,
    t: rec.timestamp
  }));

  const activity = new Activity(navigation, dbPoints);

  if (activity && activity.points?.length > 0) {
    await activity.getWindInformation().catch((error) => {
      console.warn(`Error getting wind information for activity${uid ? ` for user ${uid}` : ""}.`, error);
    });
    await activity.getLocationInformation().catch((error) => {
      console.warn(`Error getting location information for activity${uid ? ` for user ${uid}` : ""}.`, error);
    });
  }

  return activity;
};
